"use client";
import { ChecklistItem } from "@myaccount/_client/hooks";
import { useNextBestAction } from "@myaccount/_client/hooks/useNextBestAction";
import { useI18n } from "@wojo/localization";
import { Button, Card, Markdown, ProgressBar, Text } from "@wojo/ui";
import dayjs from "dayjs";
import {
    MyAccountBasicMyTripFragment,
    TripChecklistItemStatus,
} from "generated/graphql";
import { useId } from "react";
import styles from "./NextBestAction.module.scss";
import Link from "next/link";

export type NextBestActionProps = {
    trips: MyAccountBasicMyTripFragment[];
    showTourNameInTitle?: boolean;
};

export const NextBestAction: React.FC<NextBestActionProps> = ({
    trips,
    showTourNameInTitle,
}) => {
    const nextBestAction = useNextBestAction(trips);
    const headingId = useId();
    const accountNavL10n = useI18n("account").account.navigation.booking;
    const i18n = useI18n("account").account;
    const longDisplayDateFormat =
        useI18n("global").global.longDisplayDateFormat.l();

    if (!nextBestAction?.trip?.booking) {
        return null;
    }

    const gcTourName =
        nextBestAction.trip.booking.gcDeparture?.micrositeContent?.title;
    const tourName =
        gcTourName ||
        nextBestAction.trip.departure?.tourContent?.name ||
        i18n.tourNameUnavailableLabel.l();

    const tripBookingId = nextBestAction.trip.booking._id;

    let checklistUrl = accountNavL10n.tripSpecificBookingLink.l({
        bookingId: tripBookingId,
        slug: accountNavL10n.checklist.slug.l(),
        tripId: nextBestAction.trip._id,
    });

    const getDueDateMessage = (item: ChecklistItem) => {
        if (item.status === TripChecklistItemStatus.Overdue) {
            const daysDiff = dayjs().diff(item.dueDate, "days").toString();
            return i18n.nextBestAction.pastDueDateMessage.l({ daysDiff });
        } else {
            return i18n.nextBestAction.futureDueDateMessage.l({
                futureDueDate: dayjs(item.dueDate).format(
                    longDisplayDateFormat,
                ),
            });
        }
    };

    const getNextBestActionMessage = () => {
        const { item, loggedInTravelerFirstName, isLoggedInCustomerItem } =
            nextBestAction;

        if (!isLoggedInCustomerItem) {
            return i18n.nextBestAction.otherTravelerActionMessage.l({
                tourName,
                loggedInTravelerFirstName,
                otherTravelerFirstName:
                    nextBestAction.otherTravelerFirstName ||
                    i18n.nextBestAction.secondaryTravelerNameUnavailable.l(),
                checklistItemTitle: item.title,
                dueDateMessage: getDueDateMessage(item),
            });
        }

        if (nextBestAction.isSecondaryTravelerTermsChecklistItem) {
            checklistUrl = accountNavL10n.tripSpecificBookingLink.l({
                bookingId: tripBookingId,
                slug: accountNavL10n.checklist.slug.l(),
                tripId: nextBestAction.secondaryTravelerTripId,
            });
            return i18n.nextBestAction.secondaryTravelerTermsSignatureMessage.l(
                {
                    primaryTravelerFirstName:
                        nextBestAction.primaryTravelerFirstName,
                    secondaryTravelerFirstName:
                        nextBestAction.secondaryTravelerFirstName ||
                        i18n.nextBestAction.secondaryTravelerNameUnavailable.l(),
                },
            );
        }

        return i18n.nextBestAction.loggedInTravelerActionMessage.l({
            loggedInTravelerFirstName,
            checklistItemTitle: item.title,
            dueDateMessage: getDueDateMessage(item),
        });
    };

    return (
        <Card className={styles.card} tag="section" aria-labelledby={headingId}>
            <Text.Heading
                id={headingId}
                order="3"
                tag="h2"
                className={styles.heading}
            >
                {showTourNameInTitle
                    ? i18n.nextBestAction.titleWithTourName.l({
                          tourName,
                      })
                    : i18n.nextBestAction.title.l()}
            </Text.Heading>
            <Markdown
                markdown={getNextBestActionMessage()}
                className={styles["next-best-action-message"]}
            />
            <ProgressBar
                accessibleTitle={i18n.nextBestAction.accessibleTitle.l()}
                value={nextBestAction.checklistPercentageCompleted}
                positionText="bottom"
                textHorizontalAlignment="left"
                detailClassName={styles["progress-bar-detail"]}
            />
            <Button
                href={checklistUrl}
                variant="quiet"
                className={styles["view-checklist-button"]}
                tag={Link}
            >
                {i18n.nextBestAction.checklistLinkText.l()}
            </Button>
        </Card>
    );
};
